<template>
    <div>
        <!-- Лого sim delivery -->
        <div class="text-white font-bold text-2xl lg:text-4xl" style="background: rgb(78, 115, 223);">
            <a href="https://simdelivery.ru/" target="_blank">
                <div class="p-2">
                    <img :src="require('@/assets/logo.png')" class="h-12 md:h-16 mx-auto" alt="">
                </div>
            </a>
        </div>

        <!-- Сообщение об успешной отправке -->
        <div v-if="isSubmitted" class="flex items-center justify-center min-h-[50vh]">
            <div class="text-center p-8 bg-white rounded-2xl shadow-xl max-w-md mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 text-green-500 mx-auto mb-4" fill="none"
                     viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                </svg>
                <h2 class="text-2xl font-bold text-gray-800 mb-2">Форма успешно отправлена!</h2>
                <p class="text-gray-600">
                    Данные загружены для проверки менеджером.
                    <br>Ожидайте обратной связи.
                </p>
            </div>
        </div>

        <div v-else class="max-w-2xl mx-auto p-6 bg-white rounded-2xl shadow-xl space-y-6">
            <h1 class="text-2xl font-bold text-gray-800">Регистрация кандидата в курьеры SimDelivery</h1>

            <!-- Личные данные -->
            <div class="space-y-4">
                <h2 class="text-lg font-semibold text-gray-700">Личные данные</h2>

                <SystemInput
                    v-model="form.fullName"
                    label="Ф.И.О."
                />

                <SystemInput
                    v-model="form.passportNumber"
                    label="Паспорт РФ"
                    :mask="{ mask: '0000 000000', lazy: false, placeholderChar: '_' }"
                />

                <SystemInput
                    v-model="form.passportIssuedBy"
                    label="Кем выдан"
                />

                <SystemInput
                    v-model="form.passportIssuedDate"
                    label="Когда выдан"
                    :mask="{ mask: '00.00.0000', lazy: false, placeholderChar: '_' }"
                    :validate="validateDate"
                />

                <SystemInput
                    v-model="form.passportCode"
                    label="Код подразделения"
                    :mask="{ mask: '000-000', lazy: false, placeholderChar: '_' }"
                />

                <SystemInput
                    v-model="form.inn"
                    label="ИНН"
                    :mask="{ mask: '000000000000', lazy: false, placeholderChar: '_' }"
                />

                <DadataAddressSelect
                    :value="form.registrationAddress"
                    inputId="registrationAddressInputId"
                    ref="registrationAddressInput"
                    label="Адрес по прописке"
                    error="Укажите адрес"
                    @address-selected="(option) => form.registrationAddress = option.value"
                />
            </div>

            <!-- Адрес фактического проживания -->
            <div class="space-y-4">
                <h2 class="text-lg font-semibold text-gray-700">Адрес фактического проживания</h2>

                <label class="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        v-model="sameAddress"
                        @change="copyAddress"
                        class="accent-green-500"
                    />
                    <span class="unselectable">Совпадает с адресом проживания</span>
                </label>

                <div :class="{ 'disabled': sameAddress }">
                    <DadataAddressSelect
                        inputId="actualAddressInputId"
                        :value="form.actualAddress"
                        ref="actualAddressInput"
                        :label="'Город, улица, дом, квартира'"
                        error="Укажите адрес"
                        @address-selected="(option) => form.actualAddress = option.value"
                    />
                </div>
            </div>

            <!-- Фото -->
            <div class="space-y-4">
                <h2 class="text-lg font-semibold text-gray-700">Фото</h2>

                <div class="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                    <p class="text-sm text-gray-700 mb-2">
                        <span class="font-semibold text-blue-700">Требования:</span>
                        Фото разворота паспорта рядом с лицом.
                    </p>
                    <p class="text-xs text-gray-600 mb-3">
                        Допустимые форматы: JPG, JPEG, PNG.
                    </p>
                    <button
                        @click="showExampleModal = true"
                        type="button"
                        class="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
                    >
                        Посмотреть пример
                    </button>
                </div>

                <input ref="photoInput"
                       type="file"
                       accept="image/jpeg,image/jpg,image/png"
                       multiple
                       @change="handlePhotoUpload"
                       class="file-input file-input-bordered w-full"
                />
                <div v-if="photoPreviews.length" class="mt-2 grid grid-cols-5 gap-2">
                    <div v-for="(src, idx) in photoPreviews" :key="idx" class="relative photo-item w-full h-20">
                        <img :src="src" alt="Предпросмотр фото" class="w-full h-full object-contain rounded-md border"/>
                        <button
                            @click="removePhoto(idx)"
                            class="absolute top-0 right-0 m-1 p-1 bg-red-500 border text-white rounded-full w-5 h-5 flex items-center justify-center z-10"
                            title="Удалить"
                        >
                            <!-- Иконка крестик -->
                            <svg width="24px" height="24px" viewBox="0 0 24 24" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M20.7457 3.32851C20.3552 2.93798 19.722 2.93798 19.3315 3.32851L12.0371 10.6229L4.74275 3.32851C4.35223 2.93798 3.71906 2.93798 3.32854 3.32851C2.93801 3.71903 2.93801 4.3522 3.32854 4.74272L10.6229 12.0371L3.32856 19.3314C2.93803 19.722 2.93803 20.3551 3.32856 20.7457C3.71908 21.1362 4.35225 21.1362 4.74277 20.7457L12.0371 13.4513L19.3315 20.7457C19.722 21.1362 20.3552 21.1362 20.7457 20.7457C21.1362 20.3551 21.1362 19.722 20.7457 19.3315L13.4513 12.0371L20.7457 4.74272C21.1362 4.3522 21.1362 3.71903 20.7457 3.32851Z"
                                    fill="#FFFFFF"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Подтверждение -->
            <div class="space-y-5">
                <label class="flex items-start space-x-2">
                    <input type="checkbox" v-model="acceptedTerms" class="mt-1 accent-green-500"/>
                    <span>
                    Я принимаю
                    <router-link
                        to="/privacy"
                        target="_blank"
                        class="cursor-pointer"
                    >
                        условия обработки персональных данных
                    </router-link>
                </span>
                </label>

                <button
                    @click="submitForm"
                    :disabled="!formIsValid || loading"
                    :class="{ 'disabled': !formIsValid }"
                    class="w-full py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 select-none"
                >
                    Я подтверждаю корректность введённых мной данных, отправить данные
                </button>
            </div>
        </div>

        <!-- Модальное окно с примером -->
        <div
            v-if="showExampleModal"
            @click="showExampleModal = false"
            class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
        >
            <div class="relative max-w-4xl max-h-full" @click.stop>
                <button
                    @click="showExampleModal = false"
                    class="absolute -top-10 right-0 text-white hover:text-gray-300 text-xl font-bold"
                >
                    ✕ Закрыть
                </button>
                <img
                    src="@/assets/passport-example.jpg"
                    alt="Пример фото с паспортом"
                    class="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                />
            </div>
        </div>
    </div>
</template>

<script>
import DadataAddressSelect from "@/components/DadataAddressSelect.vue";
import SystemInput from "@/components/SystemInput.vue";
import axios from "axios";

export default {
    components: {SystemInput, DadataAddressSelect},
    data() {
        return {
            form: {
                fullName: '',
                passportNumber: '',
                passportIssuedBy: '',
                passportIssuedDate: '',
                passportCode: '',
                inn: '',
                registrationAddress: '',
                actualAddress: '',
                photos: [],
            },
            isSubmitted: false,
            sameAddress: false,
            photoPreviews: [],
            acceptedTerms: false,
            loading: false,
            showExampleModal: false,
        };
    },
    computed: {
        formIsValid() {
            return (
                this.form.fullName &&
                this.form.passportNumber.length === 11 &&
                this.form.passportIssuedBy &&
                this.form.passportIssuedDate &&
                this.form.passportCode &&
                this.form.inn.length === 12 &&
                this.form.registrationAddress &&
                this.form.actualAddress &&
                this.form.photos.length > 0 &&
                this.form.photos.length <= 10 &&
                this.acceptedTerms
            );
        },
    },
    methods: {
        validateDate(value) {
            const [day, month, year] = value.split('.').map(Number);
            // проверка на корректную дату
            const date = new Date(year, month - 1, day);
            const isValidDate =
                date.getFullYear() === year &&
                date.getMonth() === month - 1 &&
                date.getDate() === day;
            if (!isValidDate) return false;
            // проверка на будущее
            const today = new Date();
            return date <= today;
        },
        copyAddress() {
            if (this.sameAddress) {
                this.form.actualAddress = this.form.registrationAddress;
            } else {
                this.form.actualAddress = '';
            }
        },
        handlePhotoUpload(event) {
            const selected = Array.from(event.target.files);
            const combined = [...this.form.photos, ...selected];
            if (combined.length > 10) {
                alert('Можно загрузить не более 10 фото.');
            }
            const files = combined.slice(0, 10);
            this.form.photos = files;
            this.photoPreviews = [];
            files.forEach(file => {
                if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onload = e => this.photoPreviews.push(e.target.result);
                    reader.readAsDataURL(file);
                }
            });
            // синхронизация input.files
            const dt = new DataTransfer();
            this.form.photos.forEach(file => dt.items.add(file));
            if (this.$refs.photoInput) {
                this.$refs.photoInput.files = dt.files;
            }
        },
        removePhoto(index) {
            this.form.photos.splice(index, 1);
            this.photoPreviews.splice(index, 1);
            const dt = new DataTransfer();
            this.form.photos.forEach(file => dt.items.add(file));
            if (this.$refs.photoInput) {
                this.$refs.photoInput.files = dt.files;
            }
        },
        async submitForm() {
            if (!this.formIsValid) return;

            this.loading = true;

            const body = {
                __formId: "1",
                fullName: this.form.fullName,
                passportNumber: this.form.passportNumber,
                passportIssuedBy: this.form.passportIssuedBy,
                passportIssuedDate: this.form.passportIssuedDate.split('.').reverse().join('-'),
                passportCode: this.form.passportCode,
                inn: this.form.inn,
                registrationAddress: this.form.registrationAddress,
                actualAddress: this.form.actualAddress,
                photo: this.photoPreviews
            };

            try {
                const {data} = await axios.post("https://sim-delivery.ru/r/sendFormDataFromSite", body);

                if (!data.success) {
                    alert("Неверно заполнена форма");
                } else {
                    this.isSubmitted = true
                    this.$toast.success(
                        "Данные загружаются для проверки менеджером. \nОжидайте обратной связи",
                        {timeout: 4000}
                    );
                    this.resetForm()
                }
            } catch (err) {
                console.error(err);
                alert("Ошибка при отправке данных");
            } finally {
                this.loading = false;
            }
        },
        resetForm() {
            this.form = {
                fullName: '',
                passportNumber: '',
                passportIssuedBy: '',
                passportIssuedDate: '',
                passportCode: '',
                inn: '',
                registrationAddress: '',
                actualAddress: '',
                photos: [],
            };

            this.sameAddress = false;
            this.photoPreviews = [];
            this.acceptedTerms = false;

            if (this.$refs.photoInput) {
                this.$refs.photoInput.value = '';
            }
        },
    },
    created() {
        document.title = 'Данные кандидата на устройство';
    },
    watch: {
        'form.registrationAddress'(newVal) {
            if (this.sameAddress) {
                this.form.actualAddress = newVal;
            }
        },
    }
};
</script>

<style scoped>
.input {
    @apply border border-gray-300 rounded-lg px-4 py-2 w-full;
}

.file-input {
    @apply border border-gray-300 rounded-lg px-4 py-2 w-full;
}

.disabled {
    pointer-events: none;
    transition: opacity;
    opacity: 0.4;
}

.photo-item {
    position: relative;
}

.photo-item button {
    z-index: 10;
}


</style>
